import { useMemo, useState, useEffect, useRef } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { LEGEND_POSITIONS, LineGraph, AreaGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tab, TabList } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ISwitcherConfig } from "app/components/React/WidgetContainer/types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { GranularitySwitcherEnum } from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphTypes";
import { GRANULARITY_SWITCHER_OPTIONS } from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

import { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getPNG } from "app/UtilitiesAndConstants/UtilityFunctions/getPNG";

import { EDataType } from "../../../advertisers/overview/components/types";
import { useWebSourceSubtitleFilter } from "../../hooks/useWebSourceSubtitleFilter";
import { ContentBlur } from "../../shared/ContentBlur";
import { HookOverlayContent } from "../../shared/TableComponents";
import type { AdIntelligenceController } from "../../shared/types/types";
import {
    getNoDataObject,
    hasAdIntelligenceOverviewAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    isDailyGranularityLocked,
    openAdIntelligenceHookModal,
} from "../../utils/utils";
import { getCompareChartConfig } from "../configs/PerformanceOverTimeCompareGraphConfig";
import type { ISeriesData } from "../hooks/usePerformanceOverTimeCompareData";
import { usePerformanceOverTimeCompareData } from "../hooks/usePerformanceOverTimeCompareData";
import { CompareGraphContainer } from "../StyledComponents";
import type { GranularityKey } from "../types";
import { getCustomGranularitySwitcherOptions, granularityNameToValue } from "../utils/graphUtils";

const obfuscateGraphData = (seriesData: ISeriesData[], selectedMetric: EDataType): ISeriesData[] => {
    const shouldObfuscate = selectedMetric !== EDataType.VISITS && selectedMetric !== EDataType.PAGE_VIEWS;

    if (!shouldObfuscate) {
        return seriesData;
    }

    const MIN_VALUE = 100;
    const MAX_VALUE = 10000;

    return seriesData.map((series) => ({
        ...series,
        total: series.total ? Math.random() * (MAX_VALUE - MIN_VALUE) + MIN_VALUE : undefined,
        data: series.data.map((point) => ({
            x: point.x,
            y: Math.random() * (MAX_VALUE - MIN_VALUE) + MIN_VALUE,
            change: Math.random() * (MAX_VALUE - MIN_VALUE) + MIN_VALUE,
        })),
    }));
};

const CompareGraphWrapper = styled.div`
    margin-bottom: 24px;
    height: fit-content;
`;

const TabItemTypeToIconName: Record<EDataType, IconName> = {
    [EDataType.SPEND]: "dollar-sign",
    [EDataType.IMPRESSIONS]: "impressions",
    [EDataType.VISITS]: "chart-pie",
    [EDataType.PAGE_VIEWS]: "chart-pie",
};

type DataMode = "percent" | "number";
const DATA_MODE_PERCENT: DataMode = "percent";
const DATA_MODE_NUMBER: DataMode = "number";

interface IPerformanceOverTimeCompareGraphProps {
    controller?: AdIntelligenceController;
    defaultGranularity?: GranularitySwitcherEnum;
    hideGranularityFilter?: boolean;
    isPreview?: boolean;
}

export const PerformanceOverTimeCompareGraph = ({
    controller = "advertiser",
    defaultGranularity = GranularitySwitcherEnum.WEEKLY,
    hideGranularityFilter = false,
    isPreview = false,
}: IPerformanceOverTimeCompareGraphProps) => {
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(isPreview);
    const isHooked = isPreview && !hasAdIntelBeta;
    const thirdMetric = controller === "advertiser" ? EDataType.VISITS : EDataType.PAGE_VIEWS;
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const durationData = DurationService.getDurationData(params.duration);
    const { from, to, isWindow } = durationData.forAPI;
    const chartRef = useRef(null);

    const [selectedGranularity, setSelectedGranularity] = useState<GranularitySwitcherEnum>(defaultGranularity);
    const [selectedMetric, setSelectedMetric] = useState<EDataType>(isPreview ? thirdMetric : EDataType.SPEND);
    const [selectedDataMode, setSelectedDataMode] = useState<DataMode>(DATA_MODE_NUMBER);

    const subtitleFilters = useSubtitleFilters("date", "country");
    const websourceSubtitleFilter = useWebSourceSubtitleFilter(EDeviceType.TOTAL);
    const combinedSubtitleFilters = [...subtitleFilters, websourceSubtitleFilter];

    const currentGranularityKey = GRANULARITY_SWITCHER_OPTIONS[
        selectedGranularity
    ].value.toLowerCase() as GranularityKey;
    const { data, isLoading, isFetching, isError } = usePerformanceOverTimeCompareData(
        params,
        from,
        to,
        isWindow,
        controller,
        currentGranularityKey,
        selectedMetric,
    );
    const isGraphLoading = isLoading || isFetching;

    const hasAdIntelMetrics = data?.hasAdIntelMetrics;

    const hasNoData = data?.graphData.every((series) => series.data.length === 0) ?? true;
    const { title: noDataTitle, subtitle: noDataSubtitle } = getNoDataObject(isError, controller);

    const hideDailyGranularity = isDailyGranularityLocked();
    const customGranularitySwitcherOptions = useMemo(() => {
        return getCustomGranularitySwitcherOptions(
            params.webSource,
            hasAdIntelMetrics,
            isGraphLoading,
            isWindow,
            hideDailyGranularity,
        );
    }, [params.webSource, hasAdIntelMetrics, isGraphLoading, isWindow, hideDailyGranularity]);

    useEffect(() => {
        if (isGraphLoading) {
            return;
        }
        const currentGranularity = customGranularitySwitcherOptions.find(
            (option) => option.value === GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value,
        );
        if (!currentGranularity || currentGranularity.disabled) {
            const availableOption = [...customGranularitySwitcherOptions].reverse().find((option) => !option.disabled);

            if (availableOption) {
                setSelectedGranularity(granularityNameToValue[availableOption.value as GranularityNames]);
            }
        }
    }, [customGranularitySwitcherOptions, isGraphLoading, selectedGranularity]);

    useEffect(() => {
        if (hasAdIntelMetrics === false) {
            if (selectedMetric === EDataType.SPEND || selectedMetric === EDataType.IMPRESSIONS) {
                setSelectedMetric(thirdMetric);
            }
        }
    }, [hasAdIntelMetrics, selectedMetric, thirdMetric]);

    const handleGranularitySwitchChange = (filteredIndex: number) => {
        const item = customGranularitySwitcherOptions[filteredIndex];
        if (!item) {
            return;
        }
        const value = granularityNameToValue[item.value as GranularityNames];
        setSelectedGranularity(value);
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "switch",
            `Over Time Graph Compare/Display Advertising Performance/${item.value}`,
        );
    };

    const handleMetricChange = (metric: EDataType) => {
        setSelectedMetric(metric);
        allTrackers.dispatchTrackEvent("Metric Tab", "switch", `Over Time Graph Compare/${metric}`);
    };

    const handleDataModeChange = (index: number) => {
        const mode = index === 0 ? DATA_MODE_PERCENT : DATA_MODE_NUMBER;
        setSelectedDataMode(mode);
        allTrackers.dispatchTrackEvent("Data Mode", "switch", `Over Time Graph Compare/${mode}`);
    };

    const granularitySelectedIndex = customGranularitySwitcherOptions.findIndex(
        (option) => option.value === GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value,
    );

    const switcherGranularity: ISwitcherConfig = {
        items: customGranularitySwitcherOptions.map((item) => ({
            ...item,
            disabled: item.disabled || hasNoData,
        })),
        onItemClick: handleGranularitySwitchChange,
        initialSelectedItem: granularitySelectedIndex,
    };

    const dataModeConfig: ISwitcherConfig = {
        items: [
            { title: "%", value: DATA_MODE_PERCENT, disabled: isGraphLoading || hasNoData },
            { title: "#", value: DATA_MODE_NUMBER, disabled: isGraphLoading || hasNoData },
        ],
        onItemClick: handleDataModeChange,
        initialSelectedItem: selectedDataMode === DATA_MODE_PERCENT ? 0 : 1,
    };

    const GraphComponent = useMemo(() => {
        return selectedDataMode === DATA_MODE_PERCENT ? AreaGraph : LineGraph;
    }, [selectedDataMode]);

    const displayGraphData = useMemo(() => {
        if (!data?.graphData) {
            return undefined;
        }
        return hasAdIntelMetrics === false ? obfuscateGraphData(data.graphData, selectedMetric) : data.graphData;
    }, [data?.graphData, hasAdIntelMetrics, selectedMetric]);

    const chartConfig = useMemo(
        () =>
            getCompareChartConfig(
                GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value as GranularityNames,
                selectedMetric,
                displayGraphData ?? [],
                selectedDataMode === DATA_MODE_PERCENT,
            ),
        [selectedGranularity, selectedMetric, displayGraphData, selectedDataMode],
    );

    const showMarkers = displayGraphData?.some((series) => series.visible && series.data.length === 1) ?? false;

    const metricTabs = [
        {
            id: thirdMetric,
            label: translate(
                thirdMetric === EDataType.VISITS
                    ? "ad.intelligence.overview.performance.over.time.widget.visits"
                    : "ad.intelligence.overview.performance.over.time.widget.pageViews",
            ),
        },
        { id: EDataType.SPEND, label: translate("ad.intelligence.overview.performance.over.time.widget.spend") },
        {
            id: EDataType.IMPRESSIONS,
            label: translate("ad.intelligence.overview.performance.over.time.widget.impressions"),
        },
    ];

    return (
        <CompareGraphWrapper ref={chartRef}>
            <WidgetContainer
                data-automation="performance-overtime-compare-graph"
                title={translate("ad.intelligence.overview.performance.over.time.widget.title")}
                tooltip={translate("ad.intelligence.overview.performance.over.time.widget.tooltip")}
                subtitleFilters={combinedSubtitleFilters}
                showSubtitleFiltersLoadingState={false}
                isLoading={isGraphLoading}
                loaderWidth={"100%"}
                loaderHeight={208}
                noData={!isGraphLoading ? hasNoData : null}
                noDataTitle={noDataTitle}
                noDataSubtitle={noDataSubtitle}
                dynamicSwitcherConfig={hideGranularityFilter ? [dataModeConfig] : [dataModeConfig, switcherGranularity]}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onDownloadClick={() =>
                    chartRef.current &&
                    getPNG(
                        chartRef,
                        0,
                        50,
                        translate("ad.intelligence.overview.performance.over.time.widget.title"),
                    )?.()
                }
            >
                <TabList>
                    {metricTabs.map((tab) => {
                        const isActive = selectedMetric === tab.id;
                        const isDisabled =
                            hasAdIntelMetrics === false &&
                            (tab.id === EDataType.SPEND || tab.id === EDataType.IMPRESSIONS);

                        let iconColor: string = colorsPalettes.carbon[300];
                        if (isDisabled) {
                            iconColor = colorsPalettes.carbon[200];
                        } else if (isActive) {
                            iconColor = colorsPalettes.blue[400];
                        }

                        const iconName = TabItemTypeToIconName[tab.id];

                        return (
                            <Tab
                                key={tab.id}
                                selected={isActive}
                                disabled={isDisabled}
                                onClick={() => handleMetricChange(tab.id)}
                                tooltipText={
                                    isDisabled
                                        ? translate("ad.intelligence.overview.overtime.graph.no.metrics.tooltip")
                                        : undefined
                                }
                            >
                                <Flex
                                    alignItems="center"
                                    gap="8px"
                                    justifyContent="center"
                                >
                                    {iconName ? (
                                        <SWReactIcons
                                            iconColor={iconColor}
                                            iconName={iconName}
                                            size="xs"
                                        />
                                    ) : null}
                                    {tab.label}
                                </Flex>
                            </Tab>
                        );
                    })}
                </TabList>
                <ContentBlur
                    enabled={isHooked && selectedMetric !== EDataType.VISITS && selectedMetric !== EDataType.PAGE_VIEWS}
                    content={
                        <HookOverlayContent
                            title={translate("ad.intelligence.overview.performance.graph.preview.title")}
                            subtitle={translate("ad.intelligence.overview.performance.graph.preview.subtitle")}
                            buttonText={translate("ad.intelligence.overview.performance.graph.preview.button")}
                            isNoTouchVariant={isNoTouchVariant}
                            onButtonClick={() => openAdIntelligenceHookModal(controller)}
                            dataAutomation="hook-overlay-performance-graph"
                        />
                    }
                >
                    <CompareGraphContainer>
                        <GraphComponent<ISeriesData>
                            key={`compare-graph-${selectedMetric}-${selectedGranularity}-${selectedDataMode}`}
                            shouldPresentLegend
                            shouldPresentValueAtLegend={false}
                            isSelectableCheckboxes
                            legendPosition={LEGEND_POSITIONS.RIGHT}
                            legendsMaxWidth={160}
                            withoutTooltipsPadding={false}
                            withMarkers={showMarkers}
                            data={displayGraphData}
                            graphConfig={chartConfig}
                        />
                    </CompareGraphContainer>
                </ContentBlur>
            </WidgetContainer>
        </CompareGraphWrapper>
    );
};
